/* Cart page: quick add by SKU / part number. Looks the SKU up via predictive
   search (which indexes variants.sku), resolves the matching variant with
   parallel product lookups, adds it via cart/add.js with Section Rendering,
   and swaps the refreshed cart sections in place — no reload.

   This file is loaded via more than one <script src> tag on some pages
   (cart-drawer.liquid, rendered globally, PLUS main-cart-items.liquid on the
   real /cart page) with no shared loader to dedupe them - re-running a
   script containing top-level `class`/`let`/`const` declarations throws
   "Identifier '...' has already been declared" and aborts that whole script
   tag's execution (a real bug, found via a live buyer-journey test: the
   error appeared on the /cart page and prevented verifying the checkout
   button was present). Guarded below so a second/third <script> tag is a
   safe no-op instead of a crash. */
if (!window.__cartB2bLoaded) {
  window.__cartB2bLoaded = true;

class CartQuickAdd extends HTMLElement {
  connectedCallback() {
    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.form = this.querySelector('form');
    this.skuInput = this.querySelector('.cart-quick-add__input');
    this.qtyInput = this.querySelector('.cart-quick-add__qty');
    this.button = this.querySelector('.cart-quick-add__button');
    this.message = this.querySelector('.cart-quick-add__message');
    this.busy = false;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!this.busy) this.add();
    });
  }

  say(text, isError) {
    this.message.hidden = false;
    this.message.textContent = text;
    this.message.classList.toggle('cart-quick-add__message--error', Boolean(isError));
  }

  setBusy(busy) {
    this.busy = busy;
    this.button.disabled = busy;
  }

  async findVariantId(query) {
    const suggestUrl = `${this.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`;
    const suggest = await (await fetch(suggestUrl)).json();
    const products = suggest?.resources?.results?.products || [];
    if (!products.length) return null;
    const details = await Promise.all(
      products.map((product) =>
        fetch(`${this.root}products/${product.handle}.js`).then((r) => (r.ok ? r.json() : null))
      )
    );
    const wanted = query.toUpperCase();
    for (const detail of details) {
      const variant = detail?.variants?.find(
        (v) => (v.sku || '').toUpperCase() === wanted || (v.barcode || '').toUpperCase() === wanted
      );
      if (variant) return variant.id;
    }
    if (details.length === 1 && details[0]?.variants?.length) return details[0].variants[0].id;
    return null;
  }

  renderSections(sections) {
    Object.entries(sections || {}).forEach(([id, html]) => {
      if (!html) return;
      const incoming = new DOMParser().parseFromString(html, 'text/html');
      const target = document.getElementById(`shopify-section-${id}`) || document.getElementById(id);
      const source = incoming.getElementById(`shopify-section-${id}`) || incoming.body.firstElementChild;
      if (target && source) target.innerHTML = source.innerHTML;
    });
  }

  sectionIds() {
    const ids = [];
    document.querySelectorAll('.shopify-section').forEach((section) => {
      if (section.querySelector('#main-cart-items') || section.querySelector('#main-cart-footer')) {
        ids.push(section.id.replace('shopify-section-', ''));
      }
    });
    return ids;
  }

  async add() {
    const query = this.skuInput.value.trim();
    const quantity = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
    if (!query) return;
    this.setBusy(true);
    this.say('Searching…');
    try {
      const variantId = await this.findVariantId(query);
      if (!variantId) {
        this.say(`No product found for "${query}"`, true);
        return;
      }
      const sections = this.sectionIds();
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, sections: sections.join(',') }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        this.say(body.description || 'Could not add that item', true);
        return;
      }
      this.renderSections(body.sections);
      if (typeof publish === 'function') publish('cart-update', { source: 'cart-quick-add' });
      this.say(`Added ${quantity} × ${query.toUpperCase()}`);
      this.skuInput.value = '';
      this.skuInput.focus();
    } catch (error) {
      this.say('Something went wrong — try again', true);
    } finally {
      this.setBusy(false);
    }
  }
}

if (!customElements.get('cart-quick-add')) {
  customElements.define('cart-quick-add', CartQuickAdd);
}

/* ================= Elite cart behaviors ================= */
(function () {
  var configEl = document.querySelector('[data-cart-config]');
  var config = { undoSeconds: 6, addBehavior: 'drawer', saveForLater: true };
  try { if (configEl) config = Object.assign(config, JSON.parse(configEl.textContent)); } catch (e) {}
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function toast(message, actionLabel, onAction, seconds) {
    var el = document.querySelector('.cart-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cart-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.innerHTML = '';
    el.appendChild(document.createTextNode(message));
    if (actionLabel && onAction) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cart-toast__action';
      btn.textContent = actionLabel;
      btn.addEventListener('click', function () {
        el.classList.remove('cart-toast--visible');
        onAction();
      });
      el.appendChild(btn);
    }
    el.classList.add('cart-toast--visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(function () { el.classList.remove('cart-toast--visible'); }, (seconds || 3) * 1000);
  }

  /* Components outside this IIFE (CartBulkAdd) request a refresh via event */
  document.addEventListener('cart:refresh', function () { refreshCart(); });

  function refreshCart() {
    var ids = [];
    document.querySelectorAll('.shopify-section').forEach(function (s) {
      if (s.querySelector('#main-cart-items') || s.querySelector('#main-cart-footer')) {
        ids.push(s.id.replace('shopify-section-', ''));
      }
    });
    fetch(root + '?sections=cart-drawer,cart-icon-bubble' + (ids.length ? ',' + ids.join(',') : ''))
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        Object.keys(sections).forEach(function (id) {
          if (!sections[id]) return;
          var incoming = new DOMParser().parseFromString(sections[id], 'text/html');
          if (id === 'cart-drawer') {
            var target = document.querySelector('cart-drawer');
            var source = incoming.querySelector('cart-drawer');
            if (target && source) {
              var wasActive = target.classList.contains('active');
              target.innerHTML = source.innerHTML;
              target.classList.toggle('is-empty', source.classList.contains('is-empty'));
              if (wasActive) target.classList.add('active', 'animate');
            }
          } else if (id === 'cart-icon-bubble') {
            var bubble = document.getElementById('cart-icon-bubble');
            var src = incoming.getElementById('cart-icon-bubble') || incoming.body.firstElementChild;
            if (bubble && src) bubble.innerHTML = src.innerHTML;
          } else {
            var t = document.getElementById('shopify-section-' + id);
            var s2 = incoming.getElementById('shopify-section-' + id);
            if (t && s2) t.innerHTML = s2.innerHTML;
          }
        });
        if (typeof publish === 'function') publish('cart-update', { source: 'cart-b2b' });
      })
      .catch(function () {});
  }

  function changeLine(payload, onDone) {
    fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        if (!res.ok) toast(res.body.description || 'Could not update cart');
        refreshCart();
        if (onDone) onDone(res);
      })
      .catch(function () { toast('Could not update cart'); });
  }

  document.addEventListener('click', function (e) {
    var updateBtn = e.target.closest('[data-update-qty]');
    if (updateBtn) {
      e.preventDefault();
      changeLine({ line: parseInt(updateBtn.dataset.lineIndex, 10), quantity: parseInt(updateBtn.dataset.updateQty, 10) });
      return;
    }

    var fbt = e.target.closest('[data-fbt-add]');
    if (fbt) {
      e.preventDefault();
      fbt.disabled = true;
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(fbt.dataset.fbtAdd, 10), quantity: 1 }),
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          fbt.disabled = false;
          if (!res.ok) { toast(res.body.description || 'Could not add item'); return; }
          refreshCart();
          toast('Added to cart');
        })
        .catch(function () { fbt.disabled = false; toast('Could not add item'); });
      return;
    }

    var saveBtn = e.target.closest('[data-save-later]');
    if (saveBtn) {
      e.preventDefault();
      var saved = [];
      try { saved = JSON.parse(localStorage.getItem('saved-for-later') || '[]'); } catch (err) {}
      saved.unshift({
        id: parseInt(saveBtn.dataset.variantId, 10),
        title: saveBtn.dataset.itemTitle,
        price: saveBtn.dataset.itemPrice,
        url: saveBtn.dataset.itemUrl,
      });
      try { localStorage.setItem('saved-for-later', JSON.stringify(saved.slice(0, 20))); } catch (err) {}
      changeLine({ line: parseInt(saveBtn.dataset.lineIndex, 10), quantity: 0 }, function () {
        toast('Saved for later');
        renderSavedList();
      });
      return;
    }

    var removeBtn = e.target.closest('cart-remove-button button, .cart-remove-button');
    if (removeBtn) {
      var row = removeBtn.closest('.cart-item');
      var qi = row && row.querySelector('[data-quantity-variant-id]');
      var variantId = removeBtn.dataset.variantId || (qi && qi.dataset.quantityVariantId);
      var qtyInput = row && row.querySelector('.quantity__input');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      if (variantId) {
        setTimeout(function () {
          toast('Item removed', 'Undo', function () {
            fetch(root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty }),
            }).then(refreshCart);
          }, config.undoSeconds);
        }, 400);
      }
    }
  });

  document.addEventListener('change', function (e) {
    var swap = e.target.closest('[data-variant-swap]');
    if (!swap) return;
    var newId = parseInt(swap.value, 10);
    var qty = parseInt(swap.dataset.lineQty, 10) || 1;
    fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: swap.dataset.lineKey, quantity: 0 }),
    })
      .then(function () {
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: newId, quantity: qty }),
        });
      })
      .then(refreshCart)
      .catch(function () { toast('Could not switch option'); refreshCart(); });
  });

  var attrTimer;
  document.addEventListener('input', function (e) {
    var po = e.target.closest('[data-cart-attribute]');
    var note = e.target.closest('[data-cart-note]');
    if (!po && !note) return;
    clearTimeout(attrTimer);
    attrTimer = setTimeout(function () {
      var payload = {};
      if (po) { payload.attributes = {}; payload.attributes[po.dataset.cartAttribute] = po.value; }
      if (note) payload.note = note.value;
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(function () {});
    }, 600);
  });

  document.addEventListener('click', function (e) {
    var tgl = e.target.closest('[data-estimate-toggle]');
    if (tgl) {
      var body = tgl.parentElement.querySelector('.cart-summary__estimate-body');
      body.hidden = !body.hidden;
      tgl.setAttribute('aria-expanded', body.hidden ? 'false' : 'true');
      return;
    }
    var go = e.target.closest('[data-estimate-go]');
    if (go) {
      var wrap = go.closest('[data-shipping-estimate]');
      var zip = wrap.querySelector('[data-estimate-zip]').value.replace(/\D/g, '').slice(0, 5);
      var out = wrap.querySelector('[data-estimate-result]');
      if (zip.length !== 5) { out.textContent = 'Enter a 5-digit ZIP'; return; }
      out.textContent = 'Estimating…';
      if (window.DeliveryZip) {
        /* single code path: delivery.js owns the fetch (province + cheapest +
           TTL cache); invalidate the cache so this click is always fresh */
        try { sessionStorage.removeItem('delivery-rates-cache'); } catch (err) {}
        window.DeliveryZip.set(zip, 'cart');
        return;
      }
      /* fallback when the ZIP system is disabled */
      fetch(root + 'cart/shipping_rates.json?shipping_address%5Bzip%5D=' + zip + '&shipping_address%5Bcountry%5D=US')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var rates = data.shipping_rates || [];
          if (!rates.length) { out.textContent = 'No rates for ' + zip; return; }
          var cheapest = rates[0];
          var priceText = cheapest.price === '0.00' ? 'Free' : '$' + cheapest.price;
          out.textContent = 'Shipping from ' + priceText + ' (' + cheapest.name + ') to ' + zip + '. Tax calculated at checkout.';
        })
        .catch(function () { out.textContent = 'Could not estimate — rates appear at checkout.'; });
    }
  });

  function renderSavedList() {
    var host = document.querySelector('[data-saved-later-list]');
    if (!host) return;
    var saved = [];
    try { saved = JSON.parse(localStorage.getItem('saved-for-later') || '[]'); } catch (e) {}
    var wrap = host.closest('[data-saved-later]');
    if (wrap) wrap.hidden = saved.length === 0;
    host.innerHTML = '';
    saved.forEach(function (entry, index) {
      var li = document.createElement('li');
      li.className = 'saved-later__row';
      var a = document.createElement('a');
      a.className = 'link';
      a.href = entry.url;
      a.textContent = entry.title;
      var price = document.createElement('span');
      price.className = 'saved-later__price';
      price.textContent = entry.price;
      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'cart-fbt__add';
      add.textContent = 'Move to cart';
      add.addEventListener('click', function () {
        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: entry.id, quantity: 1 }),
        }).then(function () {
          saved.splice(index, 1);
          try { localStorage.setItem('saved-for-later', JSON.stringify(saved)); } catch (e) {}
          refreshCart();
          renderSavedList();
        });
      });
      var drop = document.createElement('button');
      drop.type = 'button';
      drop.className = 'saved-later__remove';
      drop.setAttribute('aria-label', 'Remove ' + entry.title + ' from saved items');
      drop.textContent = '×';
      drop.addEventListener('click', function () {
        saved.splice(index, 1);
        try { localStorage.setItem('saved-for-later', JSON.stringify(saved)); } catch (e) {}
        renderSavedList();
      });
      li.appendChild(a);
      li.appendChild(price);
      li.appendChild(add);
      li.appendChild(drop);
      host.appendChild(li);
    });
  }
  renderSavedList();

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-bulk-clear]')) {
      if (!window.confirm('Remove all items from your cart?')) return;
      fetch(root + 'cart/clear.js', { method: 'POST' }).then(refreshCart);
    }
    if (e.target.closest('[data-bulk-csv]')) {
      fetch(root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cartData) {
          var lines = ['SKU,Title,Quantity,Unit Price,Line Total'];
          cartData.items.forEach(function (item) {
            var title = item.product_title.split('"').join('""');
            var sku = String(item.sku || '').split('"').join('""');
            lines.push('"' + sku + '","' + title + '",' + item.quantity + ',' + (item.final_price / 100).toFixed(2) + ',' + (item.final_line_price / 100).toFixed(2));
          });
          var blob = new Blob([lines.join('\n')], { type: 'text/csv' });
          var a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'cart.csv';
          a.click();
          URL.revokeObjectURL(a.href);
        });
    }
    if (e.target.closest('[data-bulk-email]')) {
      fetch(root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cartData) {
          var body = cartData.items
            .map(function (item) { return item.quantity + ' x ' + item.product_title + ' (' + (item.sku || 'no SKU') + ') - $' + (item.final_line_price / 100).toFixed(2); })
            .join('\r\n') + '\r\nTotal: $' + (cartData.total_price / 100).toFixed(2);
          window.location.href = 'mailto:?subject=' + encodeURIComponent('Cart from ' + window.location.hostname) + '&body=' + encodeURIComponent(body);
        });
    }
  });

  if (typeof subscribe === 'function') {
    subscribe('cart-update', function (data) {
      if (data && data.cartData && data.cartData.item_count === 0 && document.getElementById('main-cart-items')) { refreshCart(); return; }
      if (!data || data.source !== 'product-form') return;
      if (config.addBehavior !== 'toast' && window.innerWidth >= 750) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      }
    });
  }

  var drawerEl = document.querySelector('cart-drawer');
  if (drawerEl) {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !drawerEl.classList.contains('active')) return;
      var focusables = drawerEl.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  if ('ResizeObserver' in window) {
    document.querySelectorAll('.header-search-inline input[type="search"]').forEach(function (input) {
      var full = input.getAttribute('placeholder');
      var ro = new ResizeObserver(function () {
        var w = input.offsetWidth; // border-box: "narrower than 480px" per spec
        if (w === 0) return; // hidden instance
        input.setAttribute('placeholder', w < 480 ? 'Search part number or keyword' : full);
      });
      ro.observe(input);
    });
  }
})();

/* Bulk quick-add: one "SKU,qty" per line; validates, adds all, reports unknowns. */
class CartBulkAdd extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound) return;
    this.dataset.bound = '1';
    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.textarea = this.querySelector('.cart-bulk-add__textarea');
    this.button = this.querySelector('[data-bulk-add-go]');
    this.message = this.querySelector('.cart-quick-add__message');
    this.button.addEventListener('click', () => this.addAll());
  }

  say(text, isError) {
    this.message.hidden = false;
    this.message.textContent = text;
    this.message.classList.toggle('cart-quick-add__message--error', Boolean(isError));
  }

  async resolveSku(sku) {
    const url = `${this.root}search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[limit]=3`;
    const suggest = await (await fetch(url)).json();
    const products = (suggest && suggest.resources && suggest.resources.results && suggest.resources.results.products) || [];
    const wanted = sku.toUpperCase();
    for (const product of products) {
      const detail = await (await fetch(`${this.root}products/${product.handle}.js`)).json();
      const variant = (detail.variants || []).find(
        (v) => (v.sku || '').toUpperCase() === wanted || (v.barcode || '').toUpperCase() === wanted
      );
      if (variant) return variant.id;
    }
    return null;
  }

  async addAll() {
    const lines = this.textarea.value
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return;
    this.button.disabled = true;
    this.say('Adding…');
    const unknown = [];
    let added = 0;
    for (const line of lines) {
      const [skuRaw, qtyRaw] = line.split(',');
      const sku = (skuRaw || '').trim();
      const qty = Math.max(1, parseInt((qtyRaw || '1').trim(), 10) || 1);
      if (!sku) continue;
      try {
        const variantId = await this.resolveSku(sku);
        if (!variantId) {
          unknown.push(sku);
          continue;
        }
        const res = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty }),
        });
        if (res.ok) added += 1;
        else unknown.push(sku);
      } catch (e) {
        unknown.push(sku);
      }
    }
    this.button.disabled = false;
    if (unknown.length) {
      this.say(`Added ${added} line${added === 1 ? '' : 's'}. Unknown SKUs: ${unknown.join(', ')}`, true);
    } else {
      this.say(`Added ${added} line${added === 1 ? '' : 's'}`);
      this.textarea.value = '';
    }
    /* Section Rendering refresh (no reload — keep the message on screen) */
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
}

if (!customElements.get('cart-bulk-add')) {
  customElements.define('cart-bulk-add', CartBulkAdd);
}

}

/* Enter in a cart quantity field updates that line instead of submitting the cart form
   (the form's first submit button is Check out, so Enter used to jump straight to checkout). */
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Enter') return;
  var input = e.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'updates[]') return;
  e.preventDefault();
  input.dispatchEvent(new Event('change', { bubbles: true }));
  input.blur();
}, true);


/* ---------- Cart add-ons: complementary products (merchant 2026-10-21) ----------
   Fills <cart-addons> from Shopify's complementary recommendations for the first
   cart line (curated per product in Search & Discovery). Keeps the server-rendered
   fallback list when Shopify returns nothing. In-stock, not excluded by tag, not
   already in the cart, at most data-limit rows. */
(function () {
  if (customElements.get('cart-addons')) return;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  function money(cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') > -1) {
      /* shop.money_format, e.g. "${{amount}}" or "{{amount}} USD"; strip any HTML wrapper */
      return format.replace(/<[^>]+>/g, '').replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100))).replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD' }).format(cents / 100);
    } catch (e) { return amount; }
  }
  customElements.define('cart-addons', class extends HTMLElement {
    connectedCallback() {
      var pid = this.dataset.productId;
      if (!pid) return;
      var limit = parseInt(this.dataset.limit, 10) || 3;
      var inCart = (this.dataset.inCart || '').split(',').filter(Boolean);
      var exclude = (this.dataset.excludeTags || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      /* print-supply brand rule against every cart line (same rule as snippets/product-hp-ok.liquid) */
      var brands = (this.dataset.brands || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      var words = (this.dataset.words || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      var lines = (this.dataset.cartLines || '').split(';;').filter(Boolean).map(function (l) { var p = l.split('|'); return { vendor: p[0] || '', type: p[1] || '' }; });
      var isSupply = function (t) { return words.some(function (w) { return t.indexOf(w) > -1; }); };
      var hpOk = function (p) {
        var rv = String(p.vendor || '').toLowerCase().trim(), rt = String(p.type || p.product_type || '').toLowerCase();
        var rBrand = brands.indexOf(rv) > -1, rSupply = isSupply(rt);
        return lines.every(function (ln) {
          var pBrand = brands.indexOf(ln.vendor) > -1, pSupply = isSupply(ln.type);
          return !((pBrand && rSupply && !rBrand) || (rBrand && rSupply && pSupply && !pBrand));
        });
      };
      var addLabel = this.dataset.addLabel || 'Add';
      var addAria = this.dataset.addAria || '[title]';
      var headingText = this.dataset.heading || '';
      var fmt = this.dataset.moneyFormat || '';
      var self = this;
      fetch(root + 'recommendations/products.json?product_id=' + encodeURIComponent(pid) + '&limit=8&intent=complementary')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          var items = (data.products || []).filter(function (p) {
            if (!p.available || !p.featured_image || inCart.indexOf(String(p.id)) > -1 || !hpOk(p)) return false;
            return !(p.tags || []).some(function (t) { return exclude.indexOf(t) > -1; });
          }).slice(0, limit);
          if (!items.length) return;
          var list = self.querySelector('[data-addons-list]');
          var heading = self.querySelector('.cart-fbt__heading');
          if (!list) return;
          list.innerHTML = '';
          items.forEach(function (p) {
            var v = (p.variants || []).filter(function (x) { return x.available; })[0] || (p.variants || [])[0];
            if (!v) return;
            var li = document.createElement('li');
            li.className = 'cart-fbt__row';
            if (p.featured_image) {
              var img = document.createElement('img');
              img.src = p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=80';
              img.alt = ''; img.width = 40; img.height = 40; img.loading = 'lazy';
              li.appendChild(img);
            } else {
              var ph = document.createElement('span'); ph.className = 'cart-fbt__thumb-placeholder'; ph.setAttribute('aria-hidden', 'true'); li.appendChild(ph);
            }
            var main = document.createElement('span'); main.className = 'cart-fbt__main';
            var a = document.createElement('a'); a.className = 'cart-fbt__title'; a.href = p.url; a.textContent = p.title; main.appendChild(a); li.appendChild(main);
            var price = document.createElement('span'); price.className = 'cart-fbt__price'; price.textContent = money(v.price, fmt); li.appendChild(price);
            var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'cart-fbt__add'; btn.setAttribute('data-fbt-add', v.id); btn.setAttribute('aria-label', addAria.replace('[title]', p.title)); btn.textContent = addLabel; li.appendChild(btn);
            list.appendChild(li);
          });
          list.hidden = false;
          if (heading) { if (headingText) heading.textContent = headingText; heading.hidden = false; }
        })
        .catch(function () {});
    }
  });
})();

/* ---------- Empty cart page: recently viewed (same localStorage key as pdp-b2b.js) ---------- */
function fillCartRecentlyViewed() {
  var wrap = document.querySelector('[data-cart-recently-viewed]');
  if (!wrap || wrap.dataset.filled) return;
  wrap.dataset.filled = '1';
  var seen = [];
  try { seen = JSON.parse(localStorage.getItem('recently-viewed') || '[]'); } catch (e) { seen = []; }
  if (!seen.length) return;
  var list = wrap.querySelector('[data-cart-recently-viewed-list]');
  var seenKeys = {};
  seen.slice(0, 16).forEach(function (item) {
    var key = item && (item.handle || (item.url || '').split('?')[0]);
    if (!item || !item.url || !item.title || !key || seenKeys[key] || list.children.length >= 8) return;
    seenKeys[key] = true;
    var li = document.createElement('li');
    li.className = 'cart-empty__recent-item';
    var a = document.createElement('a'); a.href = item.url; a.className = 'cart-empty__recent-link';
    if (item.image) { var img = document.createElement('img'); img.src = item.image; img.alt = ''; img.width = 120; img.height = 120; img.loading = 'lazy'; a.appendChild(img); }
    var t = document.createElement('span'); t.className = 'cart-empty__recent-title'; t.textContent = item.title; a.appendChild(t);
    if (item.price) { var pr = document.createElement('span'); pr.className = 'cart-empty__recent-price'; pr.textContent = item.price; a.appendChild(pr); }
    li.appendChild(a);
    list.appendChild(li);
  });
  if (list.children.length) wrap.hidden = false;
}
fillCartRecentlyViewed();
/* the cart section is re-rendered when the last line is removed: fill the fresh empty-state node too */
if (typeof subscribe === 'function') subscribe('cart-update', fillCartRecentlyViewed);
